import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Loader2, CreditCard, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PayPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const token = searchParams.get('token');
  const bookingId = searchParams.get('booking');

  useEffect(() => {
    // Auto-redirect to payment if we have valid params
    if (token && bookingId) {
      handlePayment();
    }
  }, []);

  const handlePayment = async () => {
    if (!token || !bookingId) {
      setError('Invalid payment link. Please check your email for the correct link.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API}/payments/pay-with-token`, {
        token,
        booking_id: bookingId,
        origin_url: window.location.origin
      });

      if (response.data.url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.url;
      } else {
        setError('Unable to create payment session. Please try again.');
        setLoading(false);
      }
    } catch (err) {
      console.error('Payment error:', err);
      const message = err.response?.data?.detail || 'Failed to process payment. Please try again or contact support.';
      setError(message);
      setLoading(false);
    }
  };

  if (!token || !bookingId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invalid Payment Link</h1>
          <p className="text-gray-600 mb-6">
            This payment link is invalid or has expired. Please check your email for the correct link or log in to your account.
          </p>
          <div className="space-y-3">
            <Button 
              onClick={() => navigate('/login')}
              className="w-full bg-primary hover:bg-primary/90"
            >
              Log In to Account
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/')}
              className="w-full"
            >
              Go to Homepage
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
        {loading ? (
          <>
            <Loader2 className="h-16 w-16 text-primary animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Redirecting to Payment</h1>
            <p className="text-gray-600">
              Please wait while we connect you to our secure payment processor...
            </p>
          </>
        ) : error ? (
          <>
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Error</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="space-y-3">
              <Button 
                onClick={handlePayment}
                className="w-full bg-primary hover:bg-primary/90"
              >
                Try Again
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/login')}
                className="w-full"
              >
                Log In to Account
              </Button>
            </div>
          </>
        ) : (
          <>
            <CreditCard className="h-16 w-16 text-primary mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Complete Your Payment</h1>
            <p className="text-gray-600 mb-6">
              Click below to securely complete your payment through Stripe.
            </p>
            <Button 
              onClick={handlePayment}
              className="w-full bg-green-600 hover:bg-green-700 text-lg py-6"
              data-testid="pay-now-btn"
            >
              <CreditCard className="h-5 w-5 mr-2" />
              Pay Now
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
